import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { authAPI, userRolesAPI, User } from "../services/api";
import {
  getAccessToken,
  getRefreshToken,
  setLocalAccessToken,
  setLocalRefreshToken,
  clearLocalStorage,
  getUserInfo,
  setLocalUserInfo,
} from "@/lib/storeManager";

interface AuthContextType {
  user: User | null;
  session: { access_token: string; refresh_token: string } | null;
  userRole: string | null;
  loading: boolean;
  signOut: () => Promise<void>;
  updateAuth: (
    userData: User,
    accessToken: string,
    refreshToken: string,
    jbf_role?: string
  ) => Promise<any>;
  /** Check if the current user has permission for a specific module/action */
  can: (module: string, action?: "read" | "write" | "delete") => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export { AuthContext };

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<{
    access_token: string;
    refresh_token: string;
  } | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const checkSession = useCallback(async () => {
    try {
      const accessToken = getAccessToken();
      const refreshToken = getRefreshToken();

      if (accessToken && refreshToken) {
        // Get user info from stored data (set during login/signup)
        const storedUser = getUserInfo();
        if (storedUser) {
          setUser(storedUser);
          setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          // Use jbf_role from stored user (login sets this); super_admin gets full access, management gets restricted
          if (storedUser.id) {
            const initialRole = storedUser.jbf_role ?? null;
            setUserRole(initialRole);

            // Always fetch fresh profile if we have a role
            if (initialRole) {
              await fetchUserRole(storedUser.id, initialRole);
            } else {
              await fetchUserRole(storedUser.id, undefined);
            }
          } else {
            setLoading(false);
          }
        } else {
          // No stored user info, but token exists - might be from page refresh
          setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    } catch (error) {
      // If there's an error, try to refresh token if refresh token exists
      const refreshToken = getRefreshToken();
      if (refreshToken) {
        try {
          const response = await authAPI.refreshToken(refreshToken);
          setLocalAccessToken(response.access);

          // Try to get user info from stored user
          const storedUser = getUserInfo();
          if (storedUser) {
            setUser(storedUser);
            if (storedUser.id) {
              const initialRole = storedUser.jbf_role;
              await fetchUserRole(storedUser.id, initialRole);
            } else {
              setLoading(false);
            }
          } else {
            setLoading(false);
          }

          setSession({
            access_token: response.access,
            refresh_token: refreshToken,
          });
        } catch (_refreshError) {
          // Refresh failed, clear session
          clearLocalStorage();
          setUser(null);
          setSession(null);
          setUserRole(null);
          setLoading(false);
        }
      } else {
        if (import.meta.env.DEV) {
          console.error("Error checking session:", error);
        }
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    // Check for existing session on mount
    checkSession();
  }, [checkSession]);

  const fetchUserRole = async (
    userId: string,
    initialRole?: string
  ): Promise<any> => {
    try {
      // Always fetch the full user object and role from the detail API
      let roleData: any = null;
      try {
        const res: any = await userRolesAPI.getByUserId(userId);
        roleData = res?.data ?? res;
      } catch (e) {
        if (import.meta.env.DEV) {
          console.warn("Could not fetch user-role details:", e);
        }
      }

      if (roleData?.profile?.jbf_role) {
        const foundRole = roleData.profile.jbf_role;
        setUserRole(foundRole);

        // Update user state and local storage with the full data from the user-role API
        const updatedUser = { ...roleData, jbf_role: foundRole };
        setLocalUserInfo(updatedUser);
        setUser(updatedUser);

        // Finally fetch the role-specific profile
        try {
          const profile = await authAPI.getMyProfile(foundRole);
          if (profile) {
            const profileData = (profile as any).data ?? profile;
            const mergedUser = { ...updatedUser, ...profileData };
            setUser(mergedUser);
            setLocalUserInfo(mergedUser);
            return mergedUser;
          }
        } catch (profileError) {
          if (import.meta.env.DEV) {
            console.error(
              "Error fetching profile for role:",
              foundRole,
              profileError
            );
          }
        }
        return updatedUser;
      } else {
        // Fallback for when no role profile is found but initialRole was provided
        if (initialRole) {
          setUserRole(initialRole);
          try {
            const profile = await authAPI.getMyProfile(initialRole);
            if (profile) {
              const profileData = (profile as any).data ?? profile;
              const mergedUser = {
                ...getUserInfo(),
                ...profileData,
                jbf_role: initialRole,
              };
              setUser(mergedUser);
              setLocalUserInfo(mergedUser);
              return mergedUser;
            }
          } catch (profileError) {
            if (import.meta.env.DEV) {
              console.error(
                "Error fetching profile fallback:",
                initialRole,
                profileError
              );
            }
          }
        }
        setUserRole(null);
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error in fetchUserRole flow:", error);
      }
      setUserRole(null);
    } finally {
      setLoading(false);
    }
    return null;
  };

  /**
   * Permission check logic:
   * 1. If role is 'management':
   *    - FULL rights in 'institution' module.
   *    - READ-ONLY in all other modules.
   * 2. Otherwise: default full access (can be refined).
   */
  const can = useCallback(
    (module: string, action: "read" | "write" | "delete" = "write") => {
      if (!userRole) {
        return false;
      }

      // Super Admin has all rights
      if (userRole === "super_admin") {
        return true;
      }

      // Management has restricted rights
      if (userRole === "management") {
        if (module.toLowerCase() === "institution") {
          return true; // Full rights for Institution
        }
        // Only read access for other modules
        return action === "read";
      }

      // Default: Allow read for all roles, restrict write/delete
      // Or customize based on specific role needs
      return action === "read";
    },
    [userRole]
  );

  const signOut = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error signing out:", error);
      }
    } finally {
      clearLocalStorage();
      setUser(null);
      setSession(null);
      setUserRole(null);
    }
  };

  // Expose a method to update user and session (for login/signup)
  const updateAuth = async (
    userData: User,
    accessToken: string,
    refreshToken: string,
    jbf_role?: string
  ) => {
    // Determine the role to store - check jbf_role at top level or in user data
    const actualRole = jbf_role ?? userData.jbf_role;
    setLoading(true);

    setLocalAccessToken(accessToken);
    setLocalRefreshToken(refreshToken);
    const initialUser = { ...userData, jbf_role: actualRole };
    setLocalUserInfo(initialUser);
    setUser(initialUser);
    setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    });

    if (userData?.id) {
      return await fetchUserRole(userData.id, actualRole);
    }
    setLoading(false);
    return initialUser;
  };

  return (
    <AuthContext.Provider
      value={{ user, session, userRole, loading, signOut, updateAuth, can }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// useAuth is intentionally co-exported with AuthProvider
/* eslint-disable react-refresh/only-export-components */
export const useAuth = () => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error(
      "useAuth must be used within an AuthProvider. " +
        "Make sure your component is wrapped with <AuthProvider>."
    );
  }

  return context;
};
